// src/pages/ProductPage.tsx
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiClient } from '../config/ApiClient';
import { showError, showSuccess } from '../components/Alert';
import { ItemDTO } from '../dto/item/ItemDTO';
import { ItemOptionDTO } from '../dto/item/ItemOptionDTO';
import { Button, Container, Row, Col, Spinner, Form } from 'react-bootstrap';
import ProductReviews from '../components/ProductReviews';
import { useNavigate } from 'react-router-dom';
import '../styles/ProductPage.css';
import { OrderDTO } from '../dto/order/OrderDTO';
import { isTokenValid, clearCredentials } from '../services/authUtils';
import { getDiscountedPrice } from '../utils/pricing';

const ProductPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<ItemDTO | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedOption, setSelectedOption] = useState<ItemOptionDTO | null>(
    null
  );
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [loading, setLoading] = useState(true);
  const [isAdded, setIsAdded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [quantity, setQuantity] = useState(1);

  const navigate = useNavigate();
  useEffect(() => {
    if (!id) return;

    apiClient
      .get<ItemDTO>(`/item/public/${id}`)
      .then((res) => {
        setProduct(res.data);
        setSelectedImage(res.data.thumbnail);
      })
      .catch(() => showError('Could not load product.'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleColorChange = (color: string) => {
    setSelectedColor(color);

    if (
      color &&
      selectedSize &&
      !product?.itemOptions?.some(
        (opt) => opt.color === color && opt.size === selectedSize
      )
    ) {
      setSelectedSize('');
    }
  };

  const handleSizeChange = (size: string) => {
    setSelectedSize(size);

    if (
      size &&
      selectedColor &&
      !product?.itemOptions?.some(
        (opt) => opt.size === size && opt.color === selectedColor
      )
    ) {
      setSelectedColor('');
    }
  };

  useEffect(() => {
    if (!product?.itemOptions || !selectedColor || !selectedSize) {
      setSelectedOption(null);
      return;
    }

    const match = product.itemOptions.find(
      (opt) => opt.color === selectedColor && opt.size === selectedSize
    );
    setSelectedOption(match ?? null);
  }, [product, selectedColor, selectedSize]);

  const addToCart = async (
    itemId: string,
    itemOptionId: string,
    quantity: number = 1
  ): Promise<OrderDTO | null> => {
    const token = localStorage.getItem('token');
    const userName = localStorage.getItem('userName');
    if (!token || !userName || !isTokenValid(token)) {
      clearCredentials();
      navigate('/login');
      return null;
    }

    try {
      const response = await apiClient.post<OrderDTO>('/orders/cart/add', {
        itemId,
        itemOptionId,
        quantity,
      });
      showSuccess('Item added to cart!');
      return response.data;
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to add item to cart');
      return null;
    }
  };

  const handleAddToCart = async () => {
    if (isAdded) {
      navigate('/shopping-bag');
      return;
    }
    console.log('Add@@@@');
    if (!selectedOption) {
      showError('Please select a size and color option.');
      return;
    }
    if (product == null) {
      showError('Please select a valid product.');
      return;
    }
    // Add to cart logic here

    setIsLoading(true);

    await addToCart(product.id, selectedOption.id, quantity); // default quantity = 1

    setIsAdded(true);
    setIsLoading(false);
  };

  const handlePrevImage = () => {
    if (product == null) {
      return;
    }
    const currentIndex = allImages.indexOf(selectedImage || product.thumbnail);
    const prevIndex = (currentIndex - 1 + allImages.length) % allImages.length;
    setSelectedImage(allImages[prevIndex]);
  };

  const handleNextImage = () => {
    if (product == null) {
      return;
    }
    const currentIndex = allImages.indexOf(selectedImage || product.thumbnail);
    const nextIndex = (currentIndex + 1) % allImages.length;
    setSelectedImage(allImages[nextIndex]);
  };

  if (loading) {
    return (
      <Container className="product-loading">
        <Spinner animation="border" variant="primary" />
        <p>Loading product...</p>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container>
        <p>Product not found.</p>
      </Container>
    );
  }

  const allImages = [product.thumbnail, ...(product.images || [])];
  const availableColors = Array.from(
    new Set(
      (selectedSize
        ? product.itemOptions?.filter((opt) => opt.size === selectedSize)
        : product.itemOptions
      )?.map((opt) => opt.color) || []
    )
  );
  const availableSizes = Array.from(
    new Set(
      (selectedColor
        ? product.itemOptions?.filter((opt) => opt.color === selectedColor)
        : product.itemOptions
      )?.map((opt) => opt.size) || []
    )
  );
  const basePrice = selectedOption?.price ?? product.price;
  const salePercent = selectedOption?.sale ?? product.sale ?? 0;
  const { finalPrice, hasDiscount } = getDiscountedPrice(
    basePrice,
    salePercent
  );

  return (
    <Container className="product-detail-page mt-4">
      <Row className="align-items-start">
        {/* Image + carousel */}
        <Col md={6} className="image-container position-relative">
          {allImages.length > 1 && (
            <>
              <div className="left-arrow" onClick={handlePrevImage}>
                &#10094;
              </div>
              <div className="right-arrow" onClick={handleNextImage}>
                &#10095;
              </div>
            </>
          )}
          <img
            src={selectedImage || product.thumbnail}
            alt={product.name}
            className="product-main-image mb-3"
          />
          <div className="product-carousel d-flex flex-wrap gap-2">
            {allImages.map((img, idx) => (
              <img
                key={idx}
                src={img}
                alt={`View ${idx}`}
                className={`product-thumbnail ${
                  selectedImage === img ? 'selected' : ''
                }`}
                onClick={() => setSelectedImage(img)}
              />
            ))}
          </div>
        </Col>

        {/* Product Info */}
        <Col md={6}>
          <h1>{product.name}</h1>
          <p className="product-price">
            {product.currency} {finalPrice?.toFixed(2)}
            {hasDiscount && (
              <span className="product-price-original">
                {product.currency} {basePrice.toFixed(2)}
              </span>
            )}
          </p>

          {product.description && (
            <p className="product-description">{product.description}</p>
          )}

          {/* Option Selector */}
          {product.itemOptions?.length > 0 && (
            <>
              <Form.Group controlId="colorSelect" className="mb-3">
                <Form.Label>Select Color</Form.Label>
                <Form.Select
                  onChange={(e) => handleColorChange(e.target.value)}
                  value={selectedColor}
                >
                  <option value="">Choose a color</option>
                  {availableColors.map((color) => (
                    <option key={color} value={color}>
                      {color}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group controlId="sizeSelect" className="mb-3">
                <Form.Label>Select Size</Form.Label>
                <Form.Select
                  onChange={(e) => handleSizeChange(e.target.value)}
                  value={selectedSize}
                >
                  <option value="">Choose a size</option>
                  {availableSizes.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </>
          )}
          <Form.Group controlId="quantity" className="mb-3">
            <Form.Label>Quantity</Form.Label>
            <Form.Control
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(parseInt(e.target.value))}
              style={{ maxWidth: '100px' }}
            />
          </Form.Group>
          <Button
            size="lg"
            variant="dark"
            disabled={selectedOption?.count === 0}
            onClick={handleAddToCart}
          >
            {isLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
                <span className="sr-only">Loading...</span>
              </>
            ) : isAdded ? (
              'Added to Cart - Go to Checkout'
            ) : selectedOption?.count === 0 ? (
              'Out of Stock'
            ) : (
              'Add to Cart'
            )}
          </Button>
        </Col>
      </Row>

      {/* Reviews */}
      <hr className="my-5" />
      <ProductReviews productId={product.id} />
    </Container>
  );
};

export default ProductPage;
